/**
 * HR · Workforce.
 *
 * The register every other HR figure is computed from: who works here, on
 * what contract, in which trade, on what pay — and which platform user each
 * one is, because that binding is what lets a closed work order carry the
 * cost of the person who closed it.
 *
 * Leavers stay on the register (status "left"): end of service and the audit
 * trail are both about people who have gone.
 */
import { useMemo, useState } from "react";
import { useHrPeople } from "../../api/queries";
import { useSession } from "../../app/SessionContext";
import { useScreenContext } from "../../assistant/screen";
import { Button, Card, EmptyState, Field, Input, Segmented, Select, Skeleton, Table } from "../../design/components";
import { grossPay, STATUS_LABEL } from "../../domain/hr";
import { t } from "../../i18n";
import { Page } from "../../layout/AppShell";
import { useNavigate } from "react-router-dom";
import { useHrAccess } from "./access";
import { EmployeeDialog } from "./EmployeeDialog";
import { ImportDialog } from "./ImportDialog";
import { Avatar, Chip, fileLink, h, PersonCard, PreviewBar } from "./kit";
import { Coverage, HrError, RulesBasis, s, SampleBanner, sar, SUITE, Tiles, tradeLabel } from "./shared";

type Who = "current" | "direct" | "outsourced" | "left";
const WHO: { value: Who; label: string }[] = [
  { value: "current", label: "Everyone current" },
  { value: "direct", label: "Direct" },
  { value: "outsourced", label: "Outsourced" },
  { value: "left", label: "Leavers" },
];

export function HrPeoplePage() {
  const session = useSession();
  const q = useHrPeople();
  const access = useHrAccess();
  const canEdit = access.can.write;
  const canPay = access.can.pay && p_canPay(q.data);
  const nav = useNavigate();
  const [look, setLook] = useState<"cards" | "table">(() => {
    try {
      return (localStorage.getItem("sky.hrLook") as "cards" | "table") || "cards";
    } catch {
      return "cards";
    }
  });
  const chooseLook = (v: "cards" | "table") => {
    setLook(v);
    try {
      localStorage.setItem("sky.hrLook", v);
    } catch {
      /* the choice lasts as long as the page */
    }
  };
  const [who, setWho] = useState<Who>("current");
  const [dept, setDept] = useState("");
  const [text, setText] = useState("");
  const [open, setOpen] = useState<"new" | null>(null);
  const [importing, setImporting] = useState(() => new URLSearchParams(window.location.search).get("import") === "1");
  const p = q.data;

  const current = useMemo(() => (p?.employees ?? []).filter((e) => e.status !== "left"), [p]);
  const direct = current.filter((e) => e.employment === "direct");
  const saudis = direct.filter((e) => e.saudi);
  const bound = current.filter((e) => e.principalId);
  const unpaid = current.filter((e) =>
    e.employment === "outsourced" ? !(e.outsourcedMonthly && e.outsourcedMonthly > 0) : grossPay(e) <= 0,
  );

  const rows = useMemo(() => {
    const needle = text.trim().toLowerCase();
    return (p?.employees ?? []).filter((e) => {
      if (who === "left" ? e.status !== "left" : e.status === "left") return false;
      if (who === "direct" && e.employment !== "direct") return false;
      if (who === "outsourced" && e.employment !== "outsourced") return false;
      if (dept && e.department !== dept) return false;
      if (needle && !`${e.name} ${e.employeeNo} ${e.jobTitle ?? ""}`.toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [p, who, dept, text]);

  useScreenContext(
    p
      ? {
          route: "/hr-people",
          title: "Workforce",
          summary: [
            t("{n} people on the register now: {d} direct employees and {o} outsourced.", {
              n: current.length,
              d: direct.length,
              o: current.length - direct.length,
            }),
            t("{s} of the direct employees are Saudi.", { s: saudis.length }),
            t("{b} are bound to a platform user, so the work they close is priced at their own rate.", {
              b: bound.length,
            }),
          ],
          offers: [
            { label: t("What does the workforce cost?"), ask: "what does the workforce cost a month?" },
            { label: t("Where are we on Saudization?"), ask: "what is our Saudization percentage?" },
          ],
          worksInFull: true,
        }
      : null,
  );

  return (
    <Page
      eyebrow={t("{site} · {suite}", { site: session.site.code, suite: t(SUITE) })}
      title="Workforce"
      actions={
        canEdit && p ? (
          <span style={{ display: "inline-flex", gap: 8, flexWrap: "wrap" }}>
            <Button onClick={() => setImporting(true)} data-testid="hr-import">
              📥 {t("Import from spreadsheet")}
            </Button>
            <Button variant="primary" onClick={() => setOpen("new")} data-testid="hr-add">
              {t("Add an employee")}
            </Button>
          </span>
        ) : undefined
      }
    >
      <PreviewBar />
      <SampleBanner />
      {q.isLoading && <Skeleton rows={8} />}
      {q.error && <HrError error={q.error} retry={() => void q.refetch()} />}
      {p && (
        <>
          <Tiles
            items={[
              {
                key: "heads",
                label: t("On the register now"),
                value: current.length,
                sub: t("{d} direct · {o} outsourced", { d: direct.length, o: current.length - direct.length }),
              },
              {
                key: "saudi",
                label: t("Saudi direct employees"),
                value: saudis.length,
                sub: direct.length
                  ? t("{p}% of direct heads", { p: Math.round((saudis.length / direct.length) * 100) })
                  : undefined,
              },
              {
                key: "bound",
                label: t("Bound to a platform user"),
                value: bound.length,
                sub: t("their jobs are priced at their own rate"),
              },
              {
                key: "unpaid",
                label: t("Counted, not costed"),
                value: unpaid.length,
                tone: unpaid.length ? "warn" : "good",
                sub: t("no pay or contract charge entered"),
              },
            ]}
          />
          {current.length > 0 && (
            <Coverage
              lines={[
                { label: t("have pay or a contract charge"), of: current.length, n: current.length - unpaid.length },
                { label: t("are bound to a platform user"), of: current.length, n: bound.length },
              ]}
            />
          )}
          <Card title={t("The register")} sub={t("Click anyone to open their staff file.")}>
            <div className={s.filters}>
              <Segmented value={who} options={WHO} onChange={setWho} ariaLabel="Who" />
              <Field label="Department">
                <Select value={dept} onChange={(e) => setDept(e.target.value)}>
                  <option value="">{t("All departments")}</option>
                  {p.departments.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Search">
                <Input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder={t("Name, number or title")}
                />
              </Field>
            </div>
            <div style={{ marginBottom: 10 }}>
              <Segmented
                value={look}
                options={[
                  { value: "cards", label: "Cards" },
                  { value: "table", label: "Table" },
                ]}
                onChange={chooseLook}
                ariaLabel="Show as"
              />
            </div>
            {rows.length > 0 && look === "cards" ? (
              <div className={h.people} data-testid="hr-cards">
                {rows.map((e) => (
                  <PersonCard
                    key={e.id}
                    e={e}
                    extra={
                      e.status !== "active" ? (
                        <Chip colour="var(--dim)">{t(STATUS_LABEL[e.status])}</Chip>
                      ) : e.employment === "outsourced" ? (
                        <Chip>{t("outsourced")}</Chip>
                      ) : undefined
                    }
                  />
                ))}
              </div>
            ) : rows.length === 0 ? (
              <EmptyState>
                {p.employees.length === 0
                  ? t(
                      "No one is on the register yet. Use Import from spreadsheet, or add people one by one.",
                    )
                  : t("Nobody matches.")}
              </EmptyState>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <th>{t("No.")}</th>
                    <th>{t("Name")}</th>
                    <th>{t("Nationality")}</th>
                    <th>{t("Department")}</th>
                    <th>{t("Trade")}</th>
                    <th>{t("Title")}</th>
                    <th className={s.num}>{t("Service")}</th>
                    {canPay && <th className={s.num}>{t("Monthly salary")}</th>}
                    <th>{t("GOSI")}</th>
                    <th>{t("Platform user")}</th>
                    <th>{t("Status")}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((e) => (
                    <tr
                      key={e.id}
                      onClick={() => nav(fileLink(e.id))}
                      style={{ cursor: "pointer" }}
                      data-testid={`hr-row-${e.employeeNo}`}
                    >
                      <td className={s.mono}>{e.employeeNo}</td>
                      <td>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                          <Avatar name={e.name} size={28} />
                          {e.name}
                        </span>
                      </td>
                      <td className={s.mono}>{e.nationality}</td>
                      <td>{e.department ?? "—"}</td>
                      <td>{tradeLabel(e.trade)}</td>
                      <td>
                        {e.jobTitle ?? "—"}
                        {e.employment === "outsourced" && <span className={s.tag}>{t("outsourced")}</span>}
                      </td>
                      <td className={s.num}>{t("{y} y", { y: e.serviceYears.toFixed(1) })}</td>
                      {canPay && (
                        <td className={s.num}>
                          {e.employment === "outsourced"
                            ? e.outsourcedMonthly
                              ? sar(e.outsourcedMonthly)
                              : "—"
                            : grossPay(e)
                              ? sar(grossPay(e))
                              : "—"}
                        </td>
                      )}
                      <td>
                        {e.gosiRegime ? (
                          <span title={t(e.gosiRegimeSource ?? "")}>
                            {t(e.gosiRegime)}
                            {e.gosiRegimeSource === "assumed from hire date" && <span className={s.dim}> *</span>}
                          </span>
                        ) : (
                          <span className={s.dim}>
                            {e.employment === "outsourced" ? t("contractor's") : t("hazards only")}
                          </span>
                        )}
                      </td>
                      <td>{e.principalName ?? <span className={s.dim}>—</span>}</td>
                      <td>{t(STATUS_LABEL[e.status])}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
            <p className={s.note}>
              {t(
                "* GOSI schedule based on hire date. Add the first registration date to confirm.",
              )}
            </p>
          </Card>
          <RulesBasis basis={p.rulesBasis} />
        </>
      )}
      {open && canEdit && <EmployeeDialog onClose={() => setOpen(null)} />}
      {importing && canEdit && <ImportDialog onClose={() => setImporting(false)} />}
    </Page>
  );
}

function p_canPay(p: { canPay?: boolean } | undefined): boolean {
  return p?.canPay !== false;
}
